import { Button, Input, Select, Text } from '@viapeople/components'
import type {
	Condition,
	ConditionalConfig,
	ConditionOperator,
	ConditionSourceComponentName,
} from '@viapeople/components/src/components/forms/conditionalTypes'
import {
	isConditionSource,
	OPERATOR_LABELS,
	operatorRequiresValues,
	SOURCE_OPERATOR_MAP,
} from '@viapeople/components/src/components/forms/conditionalTypes'
import React from 'react'
import {
	buildConditionalMap,
	type ConditionalTreeNode,
	detectCircularDependency,
	getTransitiveDependents,
} from '../../helpers/circularDependencyValidator'
import { useFormBuilderStore } from '../../store'
import type { PropertyEditorProps } from '../../types'

interface SourceComponent {
	id: string
	name: ConditionSourceComponentName
	label: string
	custom: unknown
}

function collectSourceComponents(
	formComponents: Array<{
		id: string
		name: string
		custom: unknown
		label: string | null
		action?: string
		children?: typeof formComponents
	}>,
): SourceComponent[] {
	const results: SourceComponent[] = []
	const walk = (comps: typeof formComponents) => {
		for (const comp of comps) {
			if (comp.action === 'delete') continue
			if (isConditionSource(comp.name)) {
				results.push({
					id: comp.id,
					name: comp.name as ConditionSourceComponentName,
					label: comp.label || comp.name,
					custom: comp.custom,
				})
			}
			if (comp.children && comp.children.length > 0) {
				walk(comp.children)
			}
		}
	}
	walk(formComponents)
	return results
}

function toggleArrayValue(values: string[], value: string): string[] {
	if (values.includes(value)) {
		return values.filter((v) => v !== value)
	}
	return [...values, value]
}

export function ConditionalConfigEditor({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
	const formComponents = useFormBuilderStore((state) => state.formComponents)
	const selectedComponentId = useFormBuilderStore((state) => state.selectedComponentId)

	const config = value as ConditionalConfig | null | undefined

	const isEnabled = config != null

	const handleEnableToggle = (checked: boolean) => {
		if (checked) {
			onChange({ compoundOperator: 'and', conditions: [] } satisfies ConditionalConfig)
		} else {
			onChange(undefined)
		}
	}

	const allSourceComponents = React.useMemo(() => collectSourceComponents(formComponents), [formComponents])

	const conditionalMap = React.useMemo(
		() => buildConditionalMap(formComponents as ConditionalTreeNode[]),
		[formComponents],
	)

	const transitiveDependents = React.useMemo(() => {
		if (!selectedComponentId) return new Set<string>()
		return getTransitiveDependents(selectedComponentId, conditionalMap)
	}, [selectedComponentId, conditionalMap])

	const availableSources = React.useMemo(() => {
		return allSourceComponents.filter((comp) => {
			if (comp.id === selectedComponentId) return false
			if (transitiveDependents.has(comp.id)) return false
			return true
		})
	}, [allSourceComponents, selectedComponentId, transitiveDependents])

	// Derive circular errors from current config state instead of tracking in useState (CQ-1 fix)
	const circularErrors = React.useMemo(() => {
		if (!config || !selectedComponentId) return {}
		const errors: Record<number, boolean> = {}
		for (let i = 0; i < config.conditions.length; i++) {
			const cond = config.conditions[i]
			if (cond.sourceComponentId !== '') {
				errors[i] = detectCircularDependency(selectedComponentId, cond.sourceComponentId, conditionalMap)
			}
		}
		return errors
	}, [config, selectedComponentId, conditionalMap])

	const handleConditionChange = (index: number, updated: Condition) => {
		if (!config) return

		const hasCircular =
			selectedComponentId != null &&
			updated.sourceComponentId !== '' &&
			detectCircularDependency(selectedComponentId, updated.sourceComponentId, conditionalMap)

		if (hasCircular) return

		const newConditions = config.conditions.map((c, i) => (i === index ? updated : c))
		onChange({ ...config, conditions: newConditions })
	}

	const handleAddCondition = () => {
		if (!config) return
		const newCondition: Condition = { sourceComponentId: '', operator: 'has_value', values: undefined }
		onChange({ ...config, conditions: [...config.conditions, newCondition] })
	}

	const handleRemoveCondition = (index: number) => {
		if (!config) return
		const newConditions = config.conditions.filter((_, i) => i !== index)
		onChange({ ...config, conditions: newConditions })
	}

	const handleCompoundOperatorChange = (val: string) => {
		if (!config) return
		onChange({ ...config, compoundOperator: val as 'and' | 'or' })
	}

	return (
		<div className='space-y-3'>
			<div className='flex items-center gap-2'>
				<input
					type='checkbox'
					id={`${name}-enable`}
					data-testid='conditional-visibility-enable'
					checked={isEnabled}
					onChange={(e) => handleEnableToggle(e.target.checked)}
					className='mr-1'
				/>
				<label htmlFor={`${name}-enable`}>
					<Text variant='small' className='text-left'>
						{label ?? 'Conditional Visibility'}
					</Text>
				</label>
			</div>

			{isEnabled && (
				<div className='space-y-3 pl-2 border-l-2 border-neutral-200'>
					{config.conditions.length >= 2 && (
						<div>
							<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
								Match
							</Text>
							<Select
								value={config.compoundOperator}
								onChange={handleCompoundOperatorChange}
								options={[
									{ value: 'and', label: 'ALL conditions (AND)' },
									{ value: 'or', label: 'ANY condition (OR)' },
								]}
								placeholder='Select'
							/>
						</div>
					)}

					{config.conditions.map((condition, index) => (
						<ConditionRow
							key={`${condition.sourceComponentId || 'empty'}-${index}`}
							index={index}
							condition={condition}
							availableSources={availableSources}
							hasCircularError={circularErrors[index] ?? false}
							onChange={(updated) => handleConditionChange(index, updated)}
							onRemove={() => handleRemoveCondition(index)}
						/>
					))}

					<Button variant='tertiary' size='small' onClick={handleAddCondition}>
						+ Add Condition
					</Button>
				</div>
			)}
		</div>
	)
}

interface ConditionRowProps {
	index: number
	condition: Condition
	availableSources: SourceComponent[]
	hasCircularError: boolean
	onChange: (condition: Condition) => void
	onRemove: () => void
}

function ConditionRow({ index, condition, availableSources, hasCircularError, onChange, onRemove }: ConditionRowProps) {
	const selectedSource = availableSources.find((s) => s.id === condition.sourceComponentId) ?? null

	const sourceOptions = availableSources.map((s) => ({
		value: s.id,
		label: s.label,
	}))

	const operatorOptions: Array<{ value: string; label: string }> = selectedSource
		? SOURCE_OPERATOR_MAP[selectedSource.name].map((op) => ({
				value: op,
				label: OPERATOR_LABELS[op],
			}))
		: []

	const handleSourceChange = (sourceId: string) => {
		const newSource = availableSources.find((s) => s.id === sourceId) ?? null
		const defaultOperator: ConditionOperator = newSource ? SOURCE_OPERATOR_MAP[newSource.name][0] : 'has_value'
		onChange({ sourceComponentId: sourceId, operator: defaultOperator, values: undefined })
	}

	const handleOperatorChange = (op: string) => {
		onChange({ ...condition, operator: op as ConditionOperator, values: undefined })
	}

	const handleValuesChange = (values: string[]) => {
		onChange({ ...condition, values: values.length > 0 ? values : undefined })
	}

	const showValuesInput = selectedSource != null && operatorRequiresValues(condition.operator)

	return (
		<div className='space-y-2 p-2 border border-neutral-200 rounded-md bg-neutral-50'>
			<div className='flex items-center justify-between'>
				<Text variant='xsmall' className='text-neutral-500'>
					Condition {index + 1}
				</Text>
				<button
					type='button'
					onClick={onRemove}
					className='text-red-500 hover:text-red-700 text-sm'
					title='Remove condition'>
					×
				</button>
			</div>

			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Source Component
				</Text>
				<Select
					value={condition.sourceComponentId}
					onChange={handleSourceChange}
					options={sourceOptions}
					placeholder='Select source'
					searchable
				/>
			</div>

			{selectedSource && (
				<div>
					<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
						Operator
					</Text>
					<div data-testid='condition-operator-select'>
						<Select
							value={condition.operator}
							onChange={handleOperatorChange}
							options={operatorOptions}
							placeholder='Select operator'
						/>
					</div>
				</div>
			)}

			{showValuesInput && (
				<ConditionValuesInput
					sourceComponent={selectedSource}
					values={condition.values ?? []}
					onChange={handleValuesChange}
				/>
			)}

			{hasCircularError && (
				<Text variant='xsmall' className='text-error-600'>
					Circular dependency detected. This source would create a loop.
				</Text>
			)}
		</div>
	)
}

interface ConditionValuesInputProps {
	sourceComponent: SourceComponent
	values: string[]
	onChange: (values: string[]) => void
}

function ConditionValuesInput({ sourceComponent, values, onChange }: ConditionValuesInputProps) {
	if (sourceComponent.name === 'select' || sourceComponent.name === 'radio-group') {
		const custom = sourceComponent.custom as { options?: Array<{ value: string | number; label: string }> }
		const options = custom?.options ?? []

		return (
			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Values
				</Text>
				{options.length === 0 ? (
					<Text variant='xsmall' className='text-neutral-400'>
						No options defined on source component
					</Text>
				) : (
					<div className='space-y-1 max-h-40 overflow-y-auto'>
						{options.map((opt) => {
							const optVal = String(opt.value)
							return (
								<div key={optVal} className='flex items-center gap-2'>
									<input
										type='checkbox'
										id={`cond-val-${sourceComponent.id}-${optVal}`}
										checked={values.includes(optVal)}
										onChange={() => onChange(toggleArrayValue(values, optVal))}
										className='w-4 h-4'
									/>
									<label htmlFor={`cond-val-${sourceComponent.id}-${optVal}`}>
										<Text variant='xsmall' className='text-left'>
											{opt.label}
										</Text>
									</label>
								</div>
							)
						})}
					</div>
				)}
			</div>
		)
	}

	if (sourceComponent.name === 'rating') {
		const custom = sourceComponent.custom as {
			count?: number
			options?: Array<{ value: string; valueLabel: string; labelColor?: string }>
		}
		const options = custom?.options ?? []

		if (options.length > 0) {
			return (
				<div>
					<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
						Values
					</Text>
					<div className='space-y-1 max-h-40 overflow-y-auto'>
						{options.map((opt) => {
							const optVal = String(opt.value)
							return (
								<div key={optVal} className='flex items-center gap-2'>
									<input
										type='checkbox'
										id={`cond-val-${sourceComponent.id}-${optVal}`}
										checked={values.includes(optVal)}
										onChange={() => onChange(toggleArrayValue(values, optVal))}
										className='w-4 h-4'
									/>
									<label htmlFor={`cond-val-${sourceComponent.id}-${optVal}`}>
										<Text variant='xsmall' className='text-left'>
											{opt.valueLabel || opt.value}
										</Text>
									</label>
								</div>
							)
						})}
					</div>
				</div>
			)
		}

		const count = Number(custom?.count ?? 5)
		const ratingValues = Array.from({ length: count }, (_, i) => String(i + 1))

		return (
			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Values
				</Text>
				<div className='flex flex-wrap gap-2'>
					{ratingValues.map((val) => (
						<div key={val} className='flex items-center gap-1'>
							<input
								type='checkbox'
								id={`cond-val-${sourceComponent.id}-${val}`}
								checked={values.includes(val)}
								onChange={() => onChange(toggleArrayValue(values, val))}
								className='w-4 h-4'
							/>
							<label htmlFor={`cond-val-${sourceComponent.id}-${val}`}>
								<Text variant='xsmall' className='text-left'>
									{val}
								</Text>
							</label>
						</div>
					))}
				</div>
			</div>
		)
	}

	if (sourceComponent.name === 'number') {
		const currentVal = values[0] ?? ''
		return (
			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Value
				</Text>
				<Input
					type='number'
					value={currentVal}
					onChange={(e) => {
						const v = e.target.value
						onChange(v !== '' ? [v] : [])
					}}
					placeholder='Enter number'
				/>
			</div>
		)
	}

	return null
}
